import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/authContext';
import { getUserBadges } from '../../services/apiservice';
import { Grid, Card, CardContent, CardMedia, Typography, Box, Chip, Stack, Skeleton, Button, Modal, IconButton } from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ExpandMore, ExpandLess, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useApiData } from '../../hooks/useApi';

const UserDashboard = () => {
  const [badges, setBadges] = useState([]);
  const { user } = useAuth();
  const userId = user.UserID;
  const { data: fetchedBadges, loading } = useApiData(() => getUserBadges(userId).then((res) => res.data), [userId]);
  const [hoveredBadgeId, setHoveredBadgeId] = useState(null);
  const [profileImageMissing, setProfileImageMissing] = useState(false);
  const [visibleCards, setVisibleCards] = useState(3);
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();


  useEffect(() => {
    if (fetchedBadges) {
      setBadges(fetchedBadges);
    }
  }, [fetchedBadges]);


  useEffect(() => {
    if (user) {
      const isProfileComplete = user.FullName !== null && user.UserPhoneNumber !== null && user.UserJobTitle !== null && user.UserGender !== null;
      if (isProfileComplete) {
        setModalOpen(false);
      } else {
        setModalOpen(true);
      }
    } else {
      console.warn("User object is undefined");
    }
  }, [user]);

  const handleApproveBadges = () => {
    navigate('/user/approve-badges', { replace: true });
  };

  // useEffect(() => {
  //   if (user) {
  //     const isProfileComplete = user.FullName  !== null && user.UserPhoneNumber  !== null && user.UserJobTitle !== null && user.UserAge !== null && user.UserGender !== null;
  //     if (isProfileComplete) {
  //       if (!user.ProfileImageUrl) {
  //         setProfileImageMissing(true); 
  //         setModalOpen(true);
  //       } else {
  //         setModalOpen(false); 
  //       }
  //     } else {
  //       setProfileImageMissing(false); 
  //       setModalOpen(true);
  //     }
  //   } else {
  //     console.warn("User  object is undefined");
  //   }
  // }, [user]);

  const handleModalClose = () => {
    setModalOpen(false);
    navigate('/user/profile');
  };

  const renderSkeleton = () => (
    <Grid item xs={12} sm={6} md={4}>
      <Card sx={{ width: '100%', borderRadius: 4, boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)' }}>
        <Skeleton
          variant="circular"
          width={150}
          height={150}
          sx={{ margin: '0 auto', mt: 2, borderRadius: '50%' }}
        />
        <CardContent>
          <Skeleton variant="text" width="60%" sx={{ height: '40px' }} />
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="80%" />
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginTop: 2 }}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="rectangular" width={50} height={30} />
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );

  const handleShowMore = () => {
    setVisibleCards(prev => Math.min(prev + 3, badges.length));
  };

  const handleShowLess = () => {
    setVisibleCards(3);
  };

  const activeBadges = badges.filter(badge => badge.AssignmentStatus === 'Active');
  const showMoreAvailable = activeBadges.length > 3;

  return (
    <Box sx={{ padding: 4 }}>
      <Helmet>
        <title>Dashboard</title>
      </Helmet>
      <Typography
        variant="h4"
        component="h2"
        sx={{
          textAlign: 'center',
          fontWeight: 700,
          fontSize: '2.5rem',
          color: '#454545',
          marginBottom: 4,
          position: 'relative',
          fontFamily: 'Roboto, sans-serif',
        }}
      >
        Your Credentials
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            bottom: '-10px',
            width: '30%',
            height: '3px',
            backgroundColor: '#42A00E',
            transform: 'translateX(-50%)',
          }}
        />
      </Typography>

      {loading ? (
        <Grid container spacing={2}>
          {Array.from(new Array(6)).map((_, index) => (
            <React.Fragment key={index}>{renderSkeleton()}</React.Fragment>
          ))}
        </Grid>
      ) : (
        <>
          {badges.length === 0 ? (
            <Typography variant="h6" sx={{ textAlign: 'center', marginTop: 4, color: 'red' }}>
              No credentials assigned.
            </Typography>
          ) :
            badges.filter(badge => badge.AssignmentStatus === 'Assigned').length > 0 ? (
              <Typography
                variant="h6"
                sx={{ textAlign: 'center', marginTop: 4, color: 'orange' }}
              >
                There are credentials assigned but not yet approved.
                <span
                  onClick={handleApproveBadges}
                  style={{
                    cursor: 'pointer',
                    color: 'blue',
                    textDecoration: 'underline',
                    transition: 'color 0.3s ease, text-decoration 0.3s ease',
                    '&:hover': {
                      color: 'darkblue',
                    },
                  }}
                >
                  {' '}Click here
                </span>
                {' '}to approve.
              </Typography>
            ) : (
              <Grid container spacing={4}>
                {activeBadges.slice(0, visibleCards).map((badge) => {
                  const isHovered = hoveredBadgeId === badge.AssignmentID;

                  return (
                    <Grid item xs={12} sm={6} md={4} key={badge.AssignmentID}>
                      <Card
                        onMouseEnter={() => setHoveredBadgeId(badge.AssignmentID)}
                        onMouseLeave={() => setHoveredBadgeId(null)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: 4,
                          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                          background: 'linear-gradient(145deg, #f5f7fa, #c3cfe2)',
                          transition: 'transform 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: '0px 6px 15px rgba(0, 0, 0, 0.2)',
                          },
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={badge.CredentialImageURL}
                          alt={badge.CredentialName}
                          sx={{
                            height: 160,
                            objectFit: 'contain',
                            padding: 2,
                          }}
                        />
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography
                            gutterBottom
                            variant="h6"
                            component="div"
                            sx={{
                              maxHeight: '1.5em',
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {badge.CredentialName}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              maxHeight: 'none',
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: isHovered ? 'none' : 2,
                              WebkitBoxOrient: 'vertical',
                              flexGrow: 1,
                            }}
                          >
                            {badge.CredentialDescription}
                          </Typography>

                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginTop: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <VisibilityIcon fontSize="small" />
                              <Typography variant="body2">{badge.AssignmentViewCount} Views</Typography>
                            </Stack>
                            <Stack direction="row" spacing={1}>
                              <Chip
                                label={badge.AssignmentStatus}
                                color={badge.AssignmentStatus === 'Active' ? 'success' : 'warning'}
                                sx={{
                                  color: 'white',
                                  boxShadow: '0px 4px 10px rgba(0, 0, 0,  0.2)',
                                  borderRadius: 1,
                                }}
                              />
                              {badge.AssignmentIsPublishedOnLinkedIn && (
                                <Chip
                                  label="LinkedIn"
                                  icon={<LinkedInIcon />}
                                  color="secondary"
                                  sx={{
                                    backgroundColor: '#0077B5',
                                    color: 'white',
                                    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
                                    borderRadius: 1,
                                    '&:hover': {
                                      backgroundColor: '#005582',
                                    },
                                  }}
                                />
                              )}
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}

          {showMoreAvailable && (
            <Box sx={{ textAlign: 'center', marginTop: 4 }}>
              {visibleCards < activeBadges.length ? (
                <Button variant="contained" onClick={handleShowMore}>
                  Show More <ExpandMore />
                </Button>
              ) : (
                <Button variant="contained" onClick={handleShowLess}>
                  Show Less <ExpandLess />
                </Button>
              )}
            </Box>
          )}
        </>
      )}

      <Modal
        open={modalOpen}
        onClose={() => { }}
        BackdropProps={{
          style: { backdropFilter: 'blur(4px)' }
        }}
      >
        <Box sx={{
          bgcolor: 'white',
          padding: 4,
          borderRadius: 2,
          boxShadow: 24,
          width: 480,
          mx: 'auto',
          mt: '20%',
          textAlign: 'center',
          position: 'relative',
        }}>
          <IconButton
            onClick={handleModalClose}
            sx={{ position: 'absolute', right: 16, top: 6, zIndex: 10 }}
          >
            <Close sx={{ color: 'black' }} />
          </IconButton>
          <Typography variant="h6" gutterBottom>
            Incomplete Profile
          </Typography>
          {/* {profileImageMissing ? (
            <Typography variant="body2" gutterBottom sx={{ color: 'red' }}>
              Please upload a profile image.
            </Typography>
          ) : ( */}
          <Typography variant="body2" gutterBottom>
            Please complete your profile before accessing the dashboard.
          </Typography>
          {/* )} */}
          <Button variant="contained" onClick={handleModalClose} sx={{ mt: 2 }}>
            Go to Profile
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default UserDashboard;
